import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, ArrowLeft, FileText, Book, Globe, Server } from 'lucide-react';

const NotFound: React.FC = () => {
  // Set page title and canonical URL
  useEffect(() => {
    document.title = 'Trang không tìm thấy - VMon Cloud';
    
    // Set canonical URL
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = 'https://vmon.vn/404';
    document.head.appendChild(canonical);
    
    return () => {
      if (canonical.parentNode) {
        canonical.parentNode.removeChild(canonical);
      }
      document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
    };
  }, []);

  const popularPages = [
    {
      title: 'VPS US - Mỹ',
      description: 'VPS giá rẻ tại Mỹ với hiệu suất cao',
      path: '/vps-us',
      icon: Globe
    },
    {
      title: 'VPS Singapore',
      description: 'VPS tốc độ cao tại Singapore',
      path: '/vps-singapore',
      icon: Globe
    },
    {
      title: 'VPS Windows',
      description: 'VPS Windows Server chuyên nghiệp',
      path: '/vps-windows',
      icon: Server
    },
    {
      title: 'Blog',
      description: 'Kiến thức về VPS và hosting',
      path: '/blog',
      icon: Book
    },
    {
      title: 'Tài liệu',
      description: 'Hướng dẫn sử dụng VPS',
      path: '/docs',
      icon: FileText
    },
    {
      title: 'Giới thiệu',
      description: 'Thông tin về VMON Cloud',
      path: '/about',
      icon: FileText
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          {/* 404 Animation */}
          <div className="relative mb-8">
            <div className="text-9xl md:text-[12rem] font-bold text-blue-100 select-none">
              404
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 bg-blue-600 rounded-full flex items-center justify-center animate-pulse">
                <Search className="h-16 w-16 text-white" />
              </div>
            </div>
          </div>

          {/* Error Message */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Trang không tìm thấy
            </h1>
            <p className="text-xl text-gray-600 mb-2">
              Xin lỗi, trang bạn đang tìm kiếm không tồn tại hoặc đã bị di chuyển.
            </p>
            <p className="text-lg text-gray-500">
              Hãy kiểm tra lại URL hoặc sử dụng các liên kết bên dưới để tiếp tục.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/"
              className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <Home className="mr-3 h-6 w-6" />
              Về trang chủ
            </Link>
            
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-8 py-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <ArrowLeft className="mr-3 h-6 w-6" />
              Quay lại
            </button>
          </div>

          {/* Popular Pages */}
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              Có thể bạn đang tìm kiếm
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularPages.map((page, index) => (
                <Link
                  key={index}
                  to={page.path}
                  className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:border-blue-200 transform hover:-translate-y-2"
                >
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                      <page.icon className="h-6 w-6 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {page.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {page.description}
                  </p>
                  <div className="mt-4 flex items-center text-blue-600 font-medium text-sm">
                    <span>Xem chi tiết</span>
                    <ArrowLeft className="ml-2 h-4 w-4 rotate-180 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-16 bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Cần hỗ trợ?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📞</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Hotline</h4>
                <p className="text-gray-600 text-sm mb-3">Hỗ trợ 24/7</p>
                <a 
                  href="tel:+84966805315" 
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  0966.805.315
                </a>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💬</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Live Chat</h4>
                <p className="text-gray-600 text-sm mb-3">Trò chuyện trực tiếp</p>
                <a 
                  href="https://m.me/VMonCloud" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Messenger
                </a>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📧</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Email</h4>
                <p className="text-gray-600 text-sm mb-3">Gửi yêu cầu hỗ trợ</p>
                <a 
                  href="mailto:support@vmon.vn" 
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  support@vmon.vn
                </a>
              </div>
            </div>
          </div>

          {/* Search Suggestion */}
          <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl text-white p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold mb-4">Tìm kiếm thông tin</h3>
            <p className="mb-6 opacity-90">
              Sử dụng tính năng tìm kiếm để tìm thông tin về VPS, hosting và các dịch vụ khác
            </p>
            <div className="flex">
              <input
                type="text"
                placeholder="Nhập từ khóa tìm kiếm..."
                className="flex-1 px-4 py-3 rounded-l-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-300"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    const searchTerm = (e.target as HTMLInputElement).value;
                    if (searchTerm.trim()) {
                      window.location.href = `/blog?search=${encodeURIComponent(searchTerm)}`;
                    }
                  }
                }}
              />
              <button 
                className="px-6 py-3 bg-white text-blue-600 rounded-r-lg hover:bg-blue-50 transition-colors font-semibold"
                onClick={() => {
                  const input = document.querySelector('input[placeholder="Nhập từ khóa tìm kiếm..."]') as HTMLInputElement;
                  const searchTerm = input?.value;
                  if (searchTerm?.trim()) {
                    window.location.href = `/blog?search=${encodeURIComponent(searchTerm)}`;
                  }
                }}
              >
                <Search className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;